// Cross-page subscription store for the backend's "actions" stream.
// The backend (G23.1+) is the authoritative source for what's currently
// happening — reboots, container restarts, zigbee toggles. Pages render
// from this store; nobody guesses via timestamps anymore.
//
// Lifecycle: startActionStream() in App.svelte once at boot. The stream
// reconnects automatically if the WebSocket drops. Initial state is
// pulled via /v2-api/actions to catch up on anything started before the
// socket connected.

import { SvelteMap } from "svelte/reactivity";
import { registerRevalidator } from "./revalidate.svelte";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

export type Phase =
  | "triggered"
  | "sent"
  | "down"
  | "recovered"
  | "settled"
  | "failed";

export interface ServerAction {
  id: string;
  kind: string;
  entity_id: string;
  phase: Phase;
  started_at: string;
  last_phase_at: string;
  reason?: string;
  attributes?: Record<string, unknown>;
}

const _actions = new SvelteMap<string, ServerAction>();
let _unsubscribe: (() => void) | undefined;
let _started = false;

function applyActionFrame(data: unknown): void {
  const a = data as ServerAction | undefined;
  if (!a || !a.entity_id || !a.phase) return;

  if (a.phase === "settled") {
    // Recovered already played the "kommt zurück…" UI for 5 s before
    // settled landed. Drop the entry so the page falls back to its
    // normal render.
    _actions.delete(a.entity_id);
    return;
  }
  if (a.phase === "failed") {
    // Keep the failure visible briefly so the user can read the
    // reason. The page renders failed differently from in-flight
    // phases (red, with reason text). After 8 s we drop it.
    _actions.set(a.entity_id, a);
    const eid = a.entity_id;
    setTimeout(() => {
      const cur = _actions.get(eid);
      if (cur && cur.phase === "failed" && cur.id === a.id) {
        _actions.delete(eid);
      }
    }, 8_000);
    return;
  }
  _actions.set(a.entity_id, a);
}

async function loadInitialSnapshot(): Promise<void> {
  try {
    const r = await fetch("/v2-api/actions", { credentials: "include" });
    if (!r.ok) return;
    const arr = (await r.json()) as ServerAction[];
    // Replace, don't merge — anything still in the local map that
    // isn't in the snapshot has settled or vanished while we were
    // disconnected. Without this, a server restart leaves stale
    // entries glowing in the UI forever.
    const fresh = new Set(arr.map((a) => a.entity_id));
    for (const eid of _actions.keys()) {
      if (!fresh.has(eid)) _actions.delete(eid);
    }
    for (const a of arr) {
      _actions.set(a.entity_id, a);
    }
  } catch {
    // ignore — the WS will catch up on the next published frame anyway
  }
}

export function startActionStream(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(loadInitialSnapshot);
  void loadInitialSnapshot().then(() => {
    if (!_started) return; // wurde inzwischen wieder gestoppt
    _unsubscribe = wsSubscribe("actions", applyActionFrame);
  });
}

export function stopActionStream(): void {
  _started = false;
  if (_unsubscribe) {
    _unsubscribe();
    _unsubscribe = undefined;
  }
  _actions.clear();
}

export function getAction(entityID: string): ServerAction | undefined {
  return _actions.get(entityID);
}
